import React, { useEffect, useMemo } from "react";
import { Stack } from "@mui/material";
import PropTypes from "prop-types";
import { useForm, FormProvider } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import CustomModal from "components/common/Modal";
import Text from "components/common/Text";
import Select from "components/common/Select";
import Button from "components/common/Button";
import BankDetailsSchema from "validations/BankDetailsSchema";

function BankDetailsModal({ open, onClose, onSave, initialData = null, banksList = [] }) {
  const bankOptions = useMemo(() => {
    if (banksList && Array.isArray(banksList)) {
      return banksList.map((bank) => ({
        value: bank.id || bank.bankId,
        label: bank.name || bank.bankName || bank,
      }));
    }
    return [];
  }, [banksList]);

  const pensionFundOptions = [
    { value: "SPP Habitat", label: "SPP Habitat" },
    { value: "SPP Integra", label: "SPP Integra" },
    { value: "SPP Prima", label: "SPP Prima" },
    { value: "SPP Profuturo", label: "SPP Profuturo" },
    { value: "ONP", label: "ONP" },
  ];

  const methods = useForm({
    resolver: yupResolver(BankDetailsSchema),
    mode: "onChange",
    defaultValues: {
      bankId: "",
      accountNumber: "",
      pensionFund: "",
    },
  });

  const {
    handleSubmit,
    reset,
    formState: { isValid },
  } = methods;

  useEffect(() => {
    if (open) {
      if (initialData) {
        reset({
          bankId: initialData.bankId || "",
          accountNumber: initialData.accountNumber || "",
          pensionFund: initialData.pensionFund || "",
        });
      } else {
        reset({
          bankId: "",
          accountNumber: "",
          pensionFund: "",
        });
      }
    }
  }, [initialData, open, reset]);

  const onSubmit = (formData) => {
    onSave(formData);
    handleCancel();
  };

  const handleCancel = () => {
    reset({
      bankId: "",
      accountNumber: "",
      pensionFund: "",
    });
    onClose();
  };

  return (
    <CustomModal
      open={open}
      onClose={handleCancel}
      title="Bank details"
      width="600px"
      padding="24px"
      sx={{ maxHeight: "90vh", overflowY: "auto" }}
    >
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack spacing={3} sx={{ mt: 3 }}>
            <Select name="bankId" label="Bank name" placeholder="Select bank" options={bankOptions} />

            <Text name="accountNumber" label="Bank account number" placeholder="Enter account number" type="text" />

            <Select
              name="pensionFund"
              label="Pension fund (AFP/ONP)"
              placeholder="Select pension fund"
              options={pensionFundOptions}
            />

            <Stack direction="row" spacing={2} sx={{ justifyContent: "flex-end", mt: 4 }}>
              <Button label="Cancel" variant="outlined" size="medium" onClick={handleCancel} />
              <Button label="Save" variant="contained" size="medium" type="submit" isDisabled={!isValid} />
            </Stack>
          </Stack>
        </form>
      </FormProvider>
    </CustomModal>
  );
}

BankDetailsModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  initialData: PropTypes.shape({
    bankId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    accountNumber: PropTypes.string,
    pensionFund: PropTypes.string,
  }),
  banksList: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        id: PropTypes.number,
        bankId: PropTypes.number,
        name: PropTypes.string,
        bankName: PropTypes.string,
      }),
    ])
  ),
};

export default BankDetailsModal;
